import * as React from 'react';
import {
    createStyles,
    Group,
    Text,
    MultiSelect as MultiSelectBase,
    MultiSelectProps,
    SelectItem as SelectItemBase
} from '@mantine/core';
import { useTranslation, Trans } from 'react-i18next';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: {
            '&.mantine-Input-wrapper': commonFieldStyles.wrapper
        },
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        readonlyRightSection: {
            display: 'none'
        },
        rootFullWidth: {
            flex: '1',
            width: '100%'
        },
        multiSelectValue: {
            [theme.fn.smallerThan('sm')]: {
                maxWidth: '13rem'
            }
        }
    };
});

export interface CustomMultiSelectProps extends MultiSelectProps, GenericFieldProps {
    key?: string;
    nothingFoundKey?: string;
    grow?: boolean;
    onChangeCb?: ({ value, changeFn }: { value: string[]; changeFn?: (value: string[]) => void }) => void;
}

export const MultiSelect: React.FC<CustomMultiSelectProps> = ({
    readOnly,
    data,
    grow,
    label,
    labelKey,
    nothingFoundKey,
    descriptionKey,
    tooltipKey,
    onChange,
    onChangeCb,
    parentform,
    targetperiod,
    highlight,
    ...props
}) => {
    const { t } = useTranslation('common');
    const { classes, cx } = useStyles();
    const selectLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const nothingFoundLabel = useFieldTranslation({
        key: nothingFoundKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });

    return (
        <MultiSelectBase
            data={data}
            classNames={{
                root: cx(classes.root, {
                    [classes.rootFullWidth]: grow
                }),
                input: cx(classes.input, {
                    [classes.readonlyInput]: readOnly,
                    [classes.inputHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                rightSection: cx('', {
                    [classes.readonlyRightSection]: readOnly
                }),
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                icon: cx(classes.icon, {
                    [classes.hiddenDisplay]: readOnly
                }),
                wrapper: cx(classes.wrapper, {
                    [classes.readonlyWrapper]: readOnly
                }),
                error: classes.error,
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                }),
                value: classes.multiSelectValue
            }}
            label={
                <>
                    <span>{selectLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            nothingFound={nothingFoundLabel}
            onChange={(value: string[]) => {
                onChange?.(value);
                onChangeCb?.({ value, changeFn: onChange });
            }}
            // placeholder={t('selectAllThatApply') as string}
            withinPortal={true}
            {...props}
        />
    );
};
